import { ChakraStylesConfig, Select, SingleValue } from 'chakra-react-select'
import {
  DARK_CARD_UNSELECTED_BORDER,
  DARK_NESTED_SURFACE,
  DARK_SURFACE_GRADIENT,
  DARK_TABLE_BORDER,
  DARK_TEXT_LIGHT,
  DARK_TEXT_MUTED
} from 'constants/dark-tokens'

// The compact select used by the banner scope switcher.
//
// Not `components/ProfileForm/DarkSelect`: that control is 44px tall by design and
// exposes no style overrides, while this one is a 28px chip. Threading a size
// variant through the shared component would put form-field concerns and banner
// concerns in one file for the sake of avoiding ~60 lines here.

/** Fixed by the design. The control never sizes to its content — see below. */
const CONTROL_WIDTH = '166px'
const CONTROL_HEIGHT = '28px'

/**
 * The control's height is built from symmetric padding rather than declared: 4 + 18
 * + 4 + 2 borders = the 28px the design asks for. Equal padding top and bottom is
 * what actually centres the text — pinning the height instead and relying on
 * react-select's own flex/grid to centre within it did not hold, and the design's
 * own numbers do not close either (5 + 18 + 5 + 2 = 30, not 28).
 */
const VALUE_LINE_HEIGHT = '18px'
const CONTROL_PADDING_Y = '4px'

/** Design's "Light" text colour for input values. */
const INPUT_TEXT = DARK_TEXT_LIGHT

export interface SwitcherSelectOption {
  value: string
  label: string
}

interface ISwitcherSelectProps {
  value: string
  options: SwitcherSelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  isDisabled?: boolean
}

const chakraStyles: ChakraStylesConfig<SwitcherSelectOption, false> = {
  container: provided => ({
    ...provided,
    // From md up the width is fixed rather than content-driven: a switcher that
    // resizes as you pick different organizations makes the whole row jump.
    // On phones the label beside it is hidden and the pair shares the screen
    // instead, so the fixed 166 would leave a gap on the right.
    width: { base: '100%', md: CONTROL_WIDTH },
    flex: { base: '1 1 0', md: '0 0 auto' },
    minWidth: 0
  }),
  control: provided => ({
    ...provided,
    minH: CONTROL_HEIGHT,
    // Height is NOT pinned — it falls out of the padding, which is what keeps the
    // text centred. See CONTROL_PADDING_Y.
    //
    // Horizontal padding lives on the inner containers instead: react-select nests
    // a value container and an indicators container inside the control, so padding
    // declared out here does not end up beside the text.
    px: 0,
    py: CONTROL_PADDING_Y,
    alignItems: 'center',
    // chakra-react-select leaves the control on the default arrow; these are
    // pickers, so say so on hover.
    cursor: 'pointer',
    borderRadius: '8px',
    background: DARK_NESTED_SURFACE,
    borderColor: DARK_CARD_UNSELECTED_BORDER,
    color: 'white',
    // No accent ring when focused/open. A tap on a phone focuses the control and
    // left react-select's blue focus border on it; the desktop look has none.
    boxShadow: 'none',
    _focus: { borderColor: DARK_CARD_UNSELECTED_BORDER, boxShadow: 'none' },
    _focusVisible: { borderColor: DARK_CARD_UNSELECTED_BORDER, boxShadow: 'none' },
    _focusWithin: { borderColor: DARK_CARD_UNSELECTED_BORDER, boxShadow: 'none' },
    _hover: { borderColor: 'rgba(194, 199, 214, 0.5)' }
  }),
  valueContainer: provided => ({ ...provided, py: 0, pl: '12px', pr: 0, alignItems: 'center' }),
  singleValue: provided => ({
    ...provided,
    color: INPUT_TEXT,
    fontFamily: 'body',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: VALUE_LINE_HEIGHT,
    m: 0,
    // One line, ellipsis on overflow — long organization names must not widen the
    // control or wrap it taller.
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  placeholder: provided => ({
    ...provided,
    color: DARK_TEXT_MUTED,
    fontFamily: 'body',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: VALUE_LINE_HEIGHT,
    m: 0
  }),
  input: provided => ({ ...provided, color: INPUT_TEXT, fontSize: '12px', m: 0 }),
  indicatorsContainer: provided => ({ ...provided, alignItems: 'center', pr: '12px' }),
  dropdownIndicator: provided => ({
    ...provided,
    px: 0,
    ml: '8px',
    // The chevron sits in its own container and does not inherit the control's
    // cursor, so the pointer would flicker back to an arrow right over the arrow.
    cursor: 'pointer',
    color: 'white',
    background: 'transparent',
    borderColor: 'transparent'
  }),
  menu: provided => ({
    ...provided,
    // The portal wrapper is sized to the control, so 100% tracks a control that
    // stretches on phones; the fixed width keeps the desktop menu at the design's.
    width: { base: '100%', md: CONTROL_WIDTH },
    background: DARK_SURFACE_GRADIENT,
    border: `1px solid ${DARK_TABLE_BORDER}`,
    borderRadius: '16px',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden'
  }),
  menuList: provided => ({
    ...provided,
    // The design's auto-layout: 12px vertical padding, 12px between items.
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    py: '12px',
    px: 0,
    background: 'transparent',
    border: 'none'
  }),
  option: (provided, state) => ({
    ...provided,
    h: '34px',
    px: '12px',
    py: '6px',
    fontFamily: 'body',
    fontSize: '12px',
    lineHeight: '18px',
    background: state.isFocused || state.isSelected ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
    color: INPUT_TEXT,
    cursor: 'pointer'
  })
}

const SwitcherSelect = ({ value, options, onChange, placeholder, isDisabled }: ISwitcherSelectProps) => {
  const selected = options.find(o => o.value === value) ?? null

  return (
    <Select<SwitcherSelectOption, false>
      options={options}
      value={selected}
      onChange={(opt: SingleValue<SwitcherSelectOption>) => onChange(opt?.value ?? '')}
      placeholder={placeholder}
      isSearchable={false}
      isDisabled={isDisabled}
      focusBorderColor={DARK_CARD_UNSELECTED_BORDER}
      // Portalled so the menu is never clipped by the banner's `overflow: hidden`
      // or trapped under the section that follows it.
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      menuPosition="fixed"
      styles={{ menuPortal: base => ({ ...base, zIndex: 12000 }) }}
      chakraStyles={chakraStyles}
    />
  )
}

export default SwitcherSelect
